/**
 * Toggle to re-enable the Google Drive onboarding step.
 * Keep the route/view in place; flip this flag to restore the step in UI + flow.
 */
export const ONBOARDING_GOOGLE_DRIVE_ENABLED = false

export const SUPPORT_EMAIL = 'support@complyone.tech'

const ALL_ONBOARDING_STEPS = [
  {
    key: 'company-overview',
    number: 1,
    label: 'Your Organization',
    summary: 'Legal name, website, country, entity type',
    route: '/onboarding/company-overview',
  },
  {
    key: 'business-overview',
    number: 2,
    label: 'Your Compliance Profile',
    summary: 'Product type, customers, corridors, licensing',
    route: '/onboarding/business-overview',
  },
  {
    key: 'cco-auditor',
    number: 3,
    label: 'Your Compliance Team',
    summary: 'CCO & Independent Reviewer',
    route: '/onboarding/cco-auditor',
  },
  {
    key: 'your-documents',
    number: 4,
    label: 'Your Documents',
    summary: 'Upload what you have — ComplyOne fills the gaps',
    route: '/onboarding/your-documents',
  },
  {
    key: 'google-drive',
    number: 5,
    label: 'Google Drive Connection',
    summary: 'Document Sync',
    route: '/onboarding/google-drive',
  },
]

export const ONBOARDING_STEPS = ALL_ONBOARDING_STEPS.filter(
  (step) => step.key !== 'google-drive' || ONBOARDING_GOOGLE_DRIVE_ENABLED,
)

export const ONBOARDING_STEP_COUNT = ONBOARDING_STEPS.length

export const ENTITY_TYPE_OPTIONS = [
  { value: 'corporation', label: 'Corporation' },
  { value: 'llc', label: 'LLC' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'other', label: 'Other' },
]

/** @deprecated Prefer PRIMARY_BUSINESS_OPTIONS — kept for persisted store / AI fill mapping */
export const COMPANY_ROLE_OPTIONS = [
  { value: 'msb', label: 'Money Services Business' },
  { value: 'fintech', label: 'Fintech' },
  { value: 'payment_processor', label: 'Payment Processor' },
  { value: 'bank', label: 'Bank' },
  { value: 'credit_union', label: 'Credit Union' },
  { value: 'lending_company', label: 'Lending Company' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
]

export const PRIMARY_BUSINESS_OPTIONS = [
  { value: 'payments_money_transfer', label: 'Payments & Money Transfer' },
  { value: 'baas', label: 'Banking as a Service (BaaS)' },
  { value: 'crypto_digital_assets', label: 'Crypto & Digital Assets' },
  { value: 'payroll_disbursements', label: 'Payroll & Disbursements' },
  { value: 'lending', label: 'Lending' },
  { value: 'neobank', label: 'Neobank' },
  { value: 'currency_exchange', label: 'Currency Exchange' },
  { value: 'other', label: 'Other' },
]

/** Alias used by existing store payload mapping */
export const FINANCIAL_PRODUCT_OPTIONS = PRIMARY_BUSINESS_OPTIONS

export const AUDIENCE_OPTIONS = [
  { value: 'individual', label: 'Individual consumers (B2C)' },
  { value: 'b2b', label: 'Businesses (B2B)' },
  { value: 'financial_institutions', label: 'Financial institutions' },
  { value: 'all', label: 'All of the above' },
]

export const OPERATING_REGION_OPTIONS = [
  { value: 'north_america', label: 'North America' },
  { value: 'latin_america', label: 'Latin America' },
  { value: 'europe', label: 'Europe' },
  { value: 'africa', label: 'Africa' },
  { value: 'asia_pacific', label: 'Asia Pacific' },
  { value: 'middle_east', label: 'Middle East' },
  { value: 'global', label: 'Global' },
]

export const BUSINESS_STAGE_OPTIONS = [
  { value: 'pre_launch', label: 'Pre-launch' },
  { value: 'soft_launch', label: 'Soft launch' },
  { value: 'live_scaling', label: 'Live and scaling' },
  { value: 'established', label: 'Established' },
]

export const CCO_EMPLOYMENT_OPTIONS = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'fractional', label: 'Fractional' },
]

export const LICENSE_TYPE_OPTIONS = [
  { value: 'msb_license', label: 'MSB License' },
  { value: 'money_transmitter', label: 'Money Transmitter License' },
  { value: 'banking_license', label: 'Banking License' },
  { value: 'payment_institution', label: 'Payment Institution License' },
  { value: 'e_money', label: 'E-Money License' },
  { value: 'other', label: 'Other' },
]

export const SETUP_DOC_FIELD_MAP = {
  formation: 'company_formation_document',
  tax: 'tax_document',
  license: 'license_document',
}

/**
 * setup_field values already collected on company-overview — skip on business step.
 */
export const COMPANY_OVERVIEW_SETUP_FIELDS = new Set([
  'legal_organization_name',
  'lagel_organization_name',
  'has_dba',
  'dba_name',
  'country_of_headquarters',
  'other_country_of_headquarters',
])

/**
 * Client-side show/hide when master-questions omit depends_on.
 * parent = parent question's setup_field, value = expected answer (case-insensitive).
 */
export const SETUP_FIELD_DEPENDENCIES = {
  dba_name: { parent: 'has_dba', value: 'Yes' },
  other_country_of_headquarters: {
    parent: 'country_of_headquarters',
    value: 'Other',
  },
  license_types: { parent: 'holds_licenses', value: 'Yes' },
  other_license_types: { parent: 'license_types', value: 'Other' },
  other_products_services: { parent: 'products_services', value: 'Other' },
  other_company_role: { parent: 'company_roles', value: 'Other' },
}

export function resolveOptionLabel(value, options) {
  return options.find((option) => option.value === value)?.label ?? value
}

export function resolveOptionLabels(values, options) {
  return values.map((value) => resolveOptionLabel(value, options))
}

export function resolveOptionValue(label, options) {
  if (!label) return null
  const normalized = String(label).trim().toLowerCase()
  const match = options.find(
    (option) =>
      option.label.toLowerCase() === normalized ||
      option.value.toLowerCase() === normalized,
  )
  return match?.value ?? null
}

export function resolveOptionValues(labels, options) {
  if (!Array.isArray(labels)) return { values: [], unmatched: [] }

  const values = []
  const unmatched = []

  labels.forEach((label) => {
    const value = resolveOptionValue(label, options)
    if (value) {
      if (!values.includes(value)) values.push(value)
      return
    }
    if (label) unmatched.push(String(label).trim())
  })

  return { values, unmatched }
}

export function parseYesNo(value) {
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined || value === '') return false
  const normalized = String(value).trim().toLowerCase()
  return normalized === 'yes' || normalized === 'true' || normalized === '1'
}

export function yesNo(value) {
  return value ? 'Yes' : 'No'
}

export function yesNoLower(value) {
  return value ? 'yes' : 'no'
}
